// src/pages/Buyer/Verification.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  CloudUpload,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  VerifiedUser,
  Warning,
  Download,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { verificationAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import toast from 'react-hot-toast';

const BuyerVerification = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [country, setCountry] = useState('India');
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [formData, setFormData] = useState({
    tax_registration_number: '',
    business_registration_certificate: '',
    fruit_license_number: '',
    import_export_license: '',
    trade_license: '',
    import_permit: '',
    company_registration: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  useEffect(() => {
    fetchVerificationStatus();
  }, []);

  const fetchVerificationStatus = async () => {
    try {
      setLoading(true);
      const response = await verificationAPI.getStatus();
      setVerificationStatus(response.data);
      
      if (response.data.user_verified) {
        setActiveStep(3); // Already verified
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
    } finally {
      setLoading(false);
    }
  };

  const countries = ['India', 'UAE'];
  const steps = country === 'India' 
    ? ['Business Details', 'Document Upload', 'Review & Submit', 'Verification Complete']
    : ['Company Details', 'Document Upload', 'Review & Submit', 'Verification Complete'];

  const handleFileUpload = (fieldName, event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const data = new FormData();
      data.append('country', country);
      
      // Add form data based on country
      if (country === 'India') {
        if (formData.tax_registration_number) {
          data.append('tax_registration_number', formData.tax_registration_number);
        }
        if (formData.business_registration_certificate) {
          data.append('business_registration_certificate', formData.business_registration_certificate);
        }
        if (formData.fruit_license_number) {
          data.append('fruit_license_number', formData.fruit_license_number);
        }
        if (formData.import_export_license) {
          data.append('import_export_license', formData.import_export_license);
        }
      } else {
        if (formData.trade_license) {
          data.append('trade_license', formData.trade_license);
        }
        if (formData.import_permit) {
          data.append('import_permit', formData.import_permit);
        }
        if (formData.company_registration) {
          data.append('company_registration', formData.company_registration);
        }
      }
      
      // Add uploaded files
      Object.keys(uploadedFiles).forEach(key => {
        if (uploadedFiles[key]) {
          data.append(key, uploadedFiles[key]);
        }
      });
      
      await verificationAPI.submit(data);
      
      toast.success('Verification submitted successfully!');
      setActiveStep(3);
      fetchVerificationStatus();
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.error || 'Verification submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DocumentUploadStep
            country={country}
            formData={formData}
            uploadedFiles={uploadedFiles}
            onFileUpload={handleFileUpload}
            onChange={handleChange}
          />
        );
      case 1:
        return (
          <ReviewStep
            country={country}
            formData={formData}
            uploadedFiles={uploadedFiles}
          />
        );
      case 2:
        return (
          <SubmissionStep
            onSubmit={handleSubmit}
            submitting={submitting}
          />
        );
      case 3:
        return <VerificationCompleteStep verificationStatus={verificationStatus} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading verification status...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Business Verification
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Complete verification to start trading on our platform
        </Typography>
      </Box>

      {verificationStatus?.user_verified ? (
        <VerificationCompleteStep verificationStatus={verificationStatus} />
      ) : (
        <>
          {/* Country Selection */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Select Your Country
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Choose your business registration country to see required documents
            </Typography>
            
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => {
                setActiveTab(newValue);
                setCountry(countries[newValue]);
                setActiveStep(0);
                setUploadedFiles({});
                setFormData({
                  tax_registration_number: '',
                  business_registration_certificate: '',
                  fruit_license_number: '',
                  import_export_license: '',
                  trade_license: '',
                  import_permit: '',
                  company_registration: '',
                });
              }}
              sx={{ mb: 3 }}
            >
              {countries.map((countryName) => (
                <Tab key={countryName} label={countryName} />
              ))}
            </Tabs>
            
            <Alert severity="info">
              <Typography variant="body2">
                {country === 'India' 
                  ? 'Indian businesses need: Tax Registration, Business Registration Certificate, Fruit License, and Import/Export License'
                  : 'UAE businesses need: Trade License, Import Permit, and Company Registration documents'}
              </Typography>
            </Alert>
          </Paper>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Paper sx={{ p: 3, mb: 3 }}>
            {renderStepContent(activeStep)}
          </Paper>

          {/* Navigation Buttons */}
          {activeStep < 3 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                disabled={activeStep === 0}
                onClick={() => setActiveStep(prev => prev - 1)}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
              
              {activeStep === steps.length - 2 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} /> : null}
                >
                  {submitting ? 'Submitting...' : 'Submit Verification'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(prev => prev + 1)}
                  disabled={!Object.keys(uploadedFiles).length}
                  endIcon={<ArrowForward />}
                >
                  Next
                </Button>
              )}
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

const DocumentUploadStep = ({ country, formData, uploadedFiles, onFileUpload, onChange }) => {
  const documents = country === 'India' 
    ? [
        {
          title: 'Tax Registration Certificate',
          fieldName: 'tax_registration_number',
          fileField: 'tax_document',
          description: 'GST Registration or equivalent tax registration certificate',
          required: true,
        },
        {
          title: 'Business Registration Certificate (BRC)',
          fieldName: 'business_registration_certificate',
          fileField: 'brc_document',
          description: 'Certificate of Incorporation or Business Registration',
          required: true,
        },
        {
          title: 'Fruit License',
          fieldName: 'fruit_license_number',
          fileField: 'fruit_license_document',
          description: 'License for fruit trading/handling',
          required: true,
        },
        {
          title: 'Import/Export License',
          fieldName: 'import_export_license',
          fileField: 'import_export_document',
          description: 'License for international fruit trade',
          required: true,
        },
      ]
    : [
        {
          title: 'Trade License',
          fieldName: 'trade_license',
          fileField: 'trade_license_document',
          description: 'Valid UAE Trade License',
          required: true,
        },
        {
          title: 'Import Permit',
          fieldName: 'import_permit',
          fileField: 'import_permit_document',
          description: 'Import permit for fruits',
          required: true,
        },
        {
          title: 'Company Registration',
          fieldName: 'company_registration',
          fileField: 'company_reg_document',
          description: 'Company registration documents',
          required: true,
        },
      ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Upload Required Documents
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please upload clear scanned copies of the following documents. All documents will be verified by our team.
      </Typography>
      
      <Grid container spacing={3}>
        {documents.map((doc) => (
          <Grid item xs={12} key={doc.fieldName}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {doc.title}
                  {doc.required && (
                    <Typography variant="caption" color="error">
                      (Required)
                    </Typography>
                  )}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {doc.description}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={`${doc.title} Number/ID`}
                      name={doc.fieldName}
                      value={formData[doc.fieldName] || ''}
                      onChange={onChange}
                      size="small"
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <input
                      accept=".pdf,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      id={`file-${doc.fileField}`}
                      type="file"
                      onChange={(e) => onFileUpload(doc.fileField, e)}
                    />
                    
                    <label htmlFor={`file-${doc.fileField}`}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUpload />}
                        fullWidth
                        size="small"
                      >
                        Upload {doc.title}
                      </Button>
                    </label>
                    
                    {uploadedFiles[doc.fileField] && (
                      <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                        ✓ {uploadedFiles[doc.fileField].name}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          • Upload clear, readable scanned copies (PDF, JPG, PNG)
          <br />
          • Maximum file size: 5MB per document
          <br />
          • All documents must be valid and current
          <br />
          • Verification typically takes 24-48 hours
        </Typography>
      </Alert>
    </Box>
  );
};

const ReviewStep = ({ country, formData, uploadedFiles }) => {
  const documents = country === 'India' 
    ? [
        { title: 'Tax Registration', fieldName: 'tax_registration_number', fileField: 'tax_document' },
        { title: 'Business Registration', fieldName: 'business_registration_certificate', fileField: 'brc_document' },
        { title: 'Fruit License', fieldName: 'fruit_license_number', fileField: 'fruit_license_document' },
        { title: 'Import/Export License', fieldName: 'import_export_license', fileField: 'import_export_document' },
      ]
    : [
        { title: 'Trade License', fieldName: 'trade_license', fileField: 'trade_license_document' },
        { title: 'Import Permit', fieldName: 'import_permit', fileField: 'import_permit_document' },
        { title: 'Company Registration', fieldName: 'company_registration', fileField: 'company_reg_document' },
      ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Your Submission
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review all information before submitting for verification.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Document Details
              </Typography>
              
              {documents.map((doc) => (
                <Box key={doc.fieldName} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight="medium" color="text.secondary">
                    {doc.title}:
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {formData[doc.fieldName] || 'Not provided'}
                  </Typography>
                  
                  {uploadedFiles[doc.fileField] && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Download fontSize="small" />
                      <Typography variant="caption">
                        {uploadedFiles[doc.fileField].name}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Verification Information
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="medium" color="text.secondary">
                  Country:
                </Typography>
                <Typography variant="body1">{country}</Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Alert severity="info">
                <Typography variant="body2">
                  After submission:
                  <br />
                  • Our team will review your documents within 24-48 hours
                  <br />
                  • You will receive email notifications about verification status
                  <br />
                  • You can track verification progress in your dashboard
                  <br />
                  • Once verified, you can start making enquiries immediately
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const SubmissionStep = ({ onSubmit, submitting }) => {
  return (
    <Box textAlign="center" py={4}>
      <VerifiedUser sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Ready to Submit?
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
        By submitting your verification documents, you confirm that all information provided is accurate and complete. 
        Our team will review your submission and update your verification status.
      </Typography>
      
      <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
        <Typography variant="body2">
          Please ensure all documents are valid and clearly readable. 
          Incomplete or unclear submissions may delay the verification process.
        </Typography>
      </Alert>
      
      <Button
        variant="contained"
        size="large"
        onClick={onSubmit}
        disabled={submitting}
        startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
        sx={{ minWidth: 200 }}
      >
        {submitting ? 'Submitting...' : 'Submit for Verification'}
      </Button>
    </Box>
  );
};

const VerificationCompleteStep = ({ verificationStatus }) => {
  return (
    <Box textAlign="center" py={4}>
      <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Verification Complete! 🎉
      </Typography>
      
      {verificationStatus?.user_verified ? (
        <>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
            Your business verification has been successfully completed. 
            You can now access all features of the Fruit Export Platform.
          </Typography>
          
          <Alert severity="success" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
            <Typography variant="body1" fontWeight="medium">
              Account Status: Verified
            </Typography>
            <Typography variant="body2">
              You can now browse fruits, make enquiries, and start trading.
            </Typography>
          </Alert>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.location.href = '/fruits'}
              sx={{ minWidth: 200 }}
            >
              Browse Fruits
            </Button>
            
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.location.href = '/buyer/dashboard'}
              sx={{ minWidth: 200 }}
            >
              Go to Dashboard
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
            Your verification request has been submitted successfully. 
            Our team will review your documents and update your status shortly.
          </Typography>
          
          <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>
            <Typography variant="body1" fontWeight="medium">
              Verification Status: Pending Review
            </Typography>
            <Typography variant="body2">
              • You will receive an email once verification is complete
              <br />
              • Typical processing time: 24-48 hours
              <br />
              • You can check status in your dashboard
            </Typography>
          </Alert>
          
          <Button
            variant="contained"
            size="large"
            onClick={() => window.location.href = '/buyer/dashboard'}
            sx={{ minWidth: 200 }}
          >
            Go to Dashboard
          </Button>
        </>
      )}
    </Box>
  );
};

export default BuyerVerification;